<script context="module" lang="ts">
	import { CacheAgeLong, CacheAgeMedium, CacheAgeShort, DBNAME, KEY_DEALS, KEY_ITEM_MERCH, KEY_PRIMARY_FILTER, KEY_SEARCH, KEY_SEARCH_VERTICAL, KEY_SECONDARY_FILTER, SEARCH_PROPKEYS, SPKEYS, createBaseLandingItemsStream, dbInfo$, getDB, loadBase, obsGeoAndFilterUpdates } from "$lib/serv/food-landing";
	import { LOCATION_PROPS, landingItemsParams$ } from "$lib/serv/locations";
	import { objectPropsEqual } from "$lib/util/object";

	export const KEY_LANDING_PAGE = 'lp';
	export const KEY_SEARCH_FOUND = 'found';

	if (browser) dbInfo$.subscribe(inf => inf && console.info(`DB ${DBNAME}:`, inf));
	
	// first isolate the geolocation updates and ignore the vertical
	const geoUpdates$ = landingItemsParams$.pipe(
		distinctUntilChanged(objectPropsEqual(...LOCATION_PROPS)),
	);

	// second modify the geolocation updates to include the vertical
	let geoUpdatesRestaurant$ = geoUpdates$.pipe(map(loc => ({ ...loc, vertical: Vertical.Restaurant})));
	let geoUpdatesStore$ = geoUpdates$.pipe(map(loc => ({ ...loc, vertical: Vertical.Store})));

	/**
	 * Util function to reduce boilerplate when bringing in the LI
	 * @param $ Location stream of your choice
	 * @returns Base LI stream
	 */
	function createBLIStream($:Observable<Partial<Address> | null>, refreshDynamicData?: Observable<any>) {
		return liCache.value$.pipe(
			switchMap(enabled => createBaseLandingItemsStream($, true, true, enabled, refreshDynamicData))
		)
	}

	const subjectRefreshDynamicData = new Subject<void>();
	export const refreshDynData = () => {
		subjectRefreshDynamicData.next();
	}

	// third create the data load streams
	export const baseLandingRestaurant$ = createBLIStream(geoUpdatesRestaurant$, subjectRefreshDynamicData).pipe(
		// tap((base) => setIfInRange(!!base[1]?.payload?.semi_static.length)), // this cannot be done here
		shareReplay(1),
	);
  export const restaurants$ = baseLandingRestaurant$.pipe(
    map((lr) => {
      const semi_static = toMap(lr[1]?.payload?.semi_static ?? []);
      const dynamic = toMap(lr[2]?.payload?.dynamic ?? []);
      return { dynamic, semi_static };
    }),
		shareReplay(1),
  );
	export const baseLandingShop$ = createBLIStream(geoUpdatesStore$, subjectRefreshDynamicData).pipe(
		// tap((base) => setIfInRange(!!base[1]?.payload?.semi_static.length)), // this cannot be done here
		shareReplay(1),
	);
  export const shops$ = baseLandingShop$.pipe(
    map((lr) => {
      const semi_static = toMap(lr[1]?.payload?.semi_static ?? []);
      const dynamic = toMap(lr[2]?.payload?.dynamic ?? []);
      return { dynamic, semi_static };
    }),
		shareReplay(1),
  );

	export const baseLanding$ = page$.pipe(
		map(p => p.url.searchParams.get('vertical')),
		distinctUntilChanged(),
		switchMap(vertical => vertical === Vertical.Store ? baseLandingShop$ : baseLandingRestaurant$),
		share(),
	)

	/**
	 * In the past the search results for and empty/initial search had tags that a customer
	 * could choose before entering a search string. These I now call quick picks. Instead
	 * of Tag.Svelte setting the TagItem in a service FoodLanding also does an
	 * initial/empty search and uses a new URL search param `found` to get the quick pick.
	 */
	const quickResult$ = combineLatest([page$, profile$]).pipe(
		map(([p,user]) => ({
			found: p.url.searchParams.get(KEY_SEARCH_FOUND),
			vert: p.url.searchParams.get(KEY_SEARCH_VERTICAL) || p.url.searchParams.get(KEY_VERTICAL) || Vertical.Restaurant,
			text: p.url.searchParams.get(KEY_SEARCH),
			lat: p.url.searchParams.get('latitude'),
			lon: p.url.searchParams.get('longitude'),
			user_id: user?.id ?? undefined,
		})),
		filter(({found}) => !!found), // on landing items we are only interested in selected search results
		distinctUntilChanged(objectPropsEqual(KEY_SEARCH_FOUND, 'vert', 'text', 'lat', 'lon', 'user_id')),
		switchMap(({found, text, vert, lat, lon, user_id}) => {
			let url = `api://search/`;
			if (vert) url += `?vertical=${vert}`;
			const obs = vert === Vertical.Store ? baseLandingShop$ : baseLandingRestaurant$;
			return obs.pipe(
				map(([, ss]) => ss?.payload?.semi_static.map(r => r.id).sort() ?? []),
				map(ids => ({ ids, hash: ids.join(',')})),
				distinctUntilKeyChanged('hash'),
				switchMap(({ids}) => fetch$<FoodResults>(
					new URL(url),
					SEARCH_PROPKEYS,
					{ db: getDB(), storeName: 'search', msMaxCacheAge: 1000 * 30 },
					{ text, meta: { lat, lon, user_id }, ids }
				)),
				map(({ loading, payload }) => {
					let tag: TagItem | undefined;
					if (payload) {
						const [type, nameSlug] = found?.split(DIVIDER) ?? [];
						payload.items.forEach(({ tiles }) => tiles.forEach(({payload:{item_type,items}}) => {
							if (item_type === type) {
								tag = (items as TagItem[]).find(({name}) => slugify(name) === nameSlug);
							}
						}))
					}
					return { loading, payload: tag };
				}),
			);
		})
	)

	const lastFoodSeachTerm$ = observe<string>('lastFoodSeachTerm');
	export const setLastFoodSeachTerm = (term?: string) => setItem('lastFoodSeachTerm', term);
	
  export type RestaurantMap = { dynamic: Map<string | number, RestaurantDynamic>, semi_static: Map<string | number, RestaurantSemiStatic> };
</script>
<script lang="ts">
  import type { Page } from "@sveltejs/kit";
  import type { Address } from "proto/address";

	import { browser } from "$app/environment";
	import {
	    LayoutSectionType,
	    type FoodLandingItemsParams,
	    type FoodResults,
	    type LandingItemContext,
	    type PrimaryResultFilter,
	    type RestaurantDynamic,
	    type RestaurantsDynamic,
	    type RestaurantSemiStatic,
	    type RestaurantsFiltered,
	    type TagItem,
	} from "$lib/def/landing";
	import { KEY_VERTICAL, Vertical, verticals } from "$lib/def/verticals";
	import { profile$ } from "$lib/serv/core/auth";
	import { fetch$, type Cache } from "$lib/serv/core/cache";
	import { liCache } from "$lib/serv/core/conf";
	import { observe, setItem } from "$lib/serv/core/local-store";
	import { extractErrorMessage, slugify } from "$lib/util/format";
	import { toMap } from "$lib/util/func";
	import { page$ } from "$lib/util/obs";
	import { hasParamValue, query } from "$lib/util/url";
	import { Observable, Subject, combineLatest, distinctUntilChanged, distinctUntilKeyChanged, filter, map, of, share, shareReplay, switchMap, tap } from "rxjs";
	import { setContext, type Component } from "svelte";
	import { fly } from "svelte/transition";
	import { DIVIDER } from "$lib/ui/home/food/menu/server";
	
	import SearchInput from "$lib/ui/home/SearchInput.svelte";
	import Header from '$lib/ui/shared/Header.svelte';
	import Sidenav from "$lib/ui/layout/Sidenav.svelte";
	import LoadingIcons from "$lib/ui/shared/LoadingIcons.svelte";
	import Banners from "$lib/ui/home/food/Banners.svelte";
	import Brands, { KEY_TILES } from "./food/Brands.svelte";
	import Deals from "./food/Deals.svelte";
	import Filter from "./food/Filter.svelte";
	import LandingPage from "$lib/ui/home/food/LandingPage.svelte";
	import Notification from "$lib/ui/home/food/Notification.svelte";
	import OutOfRange from "$lib/ui/home/food/OutOfRange.svelte";
	import SearchBody from "$lib/ui/home/food/search/Search.svelte";
	import Swimlane from "$lib/ui/home/food/Swimlane.svelte";
	import Tiles from "$lib/ui/home/food/Tiles.svelte";
  import ItemMerchGrid from "$lib/ui/home/food/ItemMerchGrid.svelte";
  import ItemMerchSwimlane from "$lib/ui/home/food/ItemMerchSwimlane.svelte";

	// export let params: { [key: string]: string } = {};
	export let page: Page;

	$: searchParams = Object.fromEntries(page.url.searchParams.entries());


	setContext<LandingItemContext>('endpoints', {
		base: `api:/`,
		cacheAge: liCache.value() ? {
			short: CacheAgeShort, medium: CacheAgeMedium, long: CacheAgeLong
		} : {
			short: -1, medium: -1, long: -1
		}
	});

	const components: { type: LayoutSectionType; component: Component<any>; }[] = [
		{ type: LayoutSectionType.FilterSelectors, component: Filter },
		{ type: LayoutSectionType.DepartmentSelectors, component: Filter },
		{ type: LayoutSectionType.Swimlane, component: Swimlane },
		{ type: LayoutSectionType.LargeBanners, component: Banners },
		{ type: LayoutSectionType.SmallBanners, component: Banners },
		{ type: LayoutSectionType.DeluxeBanner, component: Banners },
		{ type: LayoutSectionType.BrandLogos, component: Brands },
		{ type: LayoutSectionType.AllRestaurants, component: Tiles },
	];

	/**
	 * !! Super mega important for shops filtering !!
	 * 
	 * Shops is filtered directly by IDs returned from department_selectors call (restaurants make an extra call to get filter IDs)
	 * This is added on load and when filters change:
	 */
	let directFilterIDs: number[] | undefined | null = null;

	// we now also use `directFilterIDs` to filter the tiles (restaurant ids for a brand)
	$: if (page.url.searchParams.get(KEY_TILES)) directFilterIDs = page.url.searchParams.get(KEY_TILES)?.split(',').map(Number);

	// Make sure the vertical is 'store' for the browse all button in the sidenav
	$: isShop = query(page.url).hasValue('vertical', Vertical.Store);

	let primaryFilters: PrimaryResultFilter[];

	const makeID = (item: PrimaryResultFilter) => item.type === 'filter' ? item.term as string : item.type;

	function onLoadedPrimaryFilters(list: PrimaryResultFilter[]) {

		primaryFilters = list;

		if (searchParams.primary_food_filter) {
			directFilterIDs = list.find((filter) => filter.term === searchParams.primary_food_filter)?.store_ids ?? null;
		} else {
			directFilterIDs = null;
		}

		// const makeID = (item: PrimaryResultFilter) => item.type === 'filter' ? item.term as string : item.type;
		// const filteredList = list?.filter((item) => item.type !== 'link');

	}

	/**
	 * Filter IDs from query params
	 */
	const filtered$ = obsGeoAndFilterUpdates(landingItemsParams$).pipe(
		switchMap((params) =>
			(params.primary_food_filter || params.secondary_filters || params.deals) ? fetchFiltered(params) : of(null)
		),
	);

	const fetchFiltered = (params: FoodLandingItemsParams) => loadBase(params, true).pipe(
		switchMap((cache) =>
			cache?.payload?.restaurants.filtered
			&& fetch$<RestaurantsFiltered>(new URL(`api:/${cache?.payload?.restaurants.filtered}`), SPKEYS, { db: getDB(), msMaxCacheAge: CacheAgeMedium })
			|| of({ ...cache, payload: undefined } as Cache<RestaurantsFiltered>)
		),
		map((cache) => ({ ...cache, payload: cache?.payload?.filtered })),
		filter((cache) => !!cache.payload ? true : (cache.loading || cache.error)),
	);

	const actionNotification = (note:{type:string}) => {
		if (note.type === 'capacity_delay') refreshDynData();
	}

	let openMenuModal = false;

	function reviseDynamicTilesForSeoGroupStack(group: string | null, dynamic: Cache<RestaurantsDynamic> | null, semi_static: RestaurantSemiStatic[] | undefined) {
		if (!dynamic?.payload || !group || isNaN(+group) || !semi_static) return dynamic;
		const groupId = +group;
		// if layout already has a stack with the groupId then return dynamic
		if (dynamic.payload.tiles?.some(t => t.type === 'stack' && t.payload.group_id === groupId)) return dynamic;
		// make a new dynamic payload with tiles containing a new stack for this group
		const tile = {
			type: 'stack',
			payload: {
				title: 'Near you',
				restaurant_ids: semi_static.filter((item) => item.restaurant_group?.id === groupId).map((item) => item.id),
				online: true,
				open: true,
				group_id: groupId,
			},
		}
		return {
			...dynamic,
			payload: {
				...dynamic.payload,
				tiles: [
					...dynamic.payload.tiles,
					tile
				],
			},
		} as Cache<RestaurantsDynamic>;
	}
</script>

<SearchInput placeholder={query(page.url).hasValue(KEY_VERTICAL, Vertical.Store) ? 'Search shops, items' : 'Search restaurants, dishes'} currentPageUrl={page.url} />

{#if primaryFilters}
	<Sidenav {page} bind:open={openMenuModal}>
		{#if isShop}
			{@const active = !query(page.url).hasAny(KEY_PRIMARY_FILTER, KEY_SEARCH) && page.url.pathname === '/'}
			<a href={`/?${query(page.url).keep(...LOCATION_PROPS, KEY_VERTICAL).remove(KEY_SEARCH)}`} class="label-xl book ellipses" class:active><span class="icon-house-20"></span> Browse all</a>
		{/if}
		{#each primaryFilters as item, i}
			{@const href = '/?' + query(page.url).keep(...LOCATION_PROPS, 'vertical').toggle(KEY_PRIMARY_FILTER, makeID(item).toString())}
			{@const active = hasParamValue(page.url, KEY_PRIMARY_FILTER, makeID(item).toString())}
			<a
				href={href}
				data-sveltekit-replacestate={query(page.url).has(KEY_PRIMARY_FILTER)}
				on:click={() => openMenuModal = false}
				class="label-xl book ellipses"
				class:active
				in:fly={{ x: 20, delay: i * 20 }}
				out:fly={{ x: -20, delay: i * 20 }}
			>{item.name}</a>
		{/each}
		<!-- <svelte:fragment slot="categories">&nbsp;</svelte:fragment> -->
		{#snippet categories()}{/snippet}
	</Sidenav>
{/if}

<!-- prevents padding on top of content -->
<div>
	{#if query(page.url).has('search') && !query(page.url).has(KEY_SEARCH_FOUND)}
		<SearchBody {page} />
	{:else if $baseLanding$}
		{@const vertical = page.url.searchParams.get('vertical') || Vertical.Restaurant}
		{@const restaurantId = page.url.searchParams.get('restaurant')}
		{@const [layout, semistatic, dynamic, brands, deals] = $baseLanding$}
    {@const restaurants = { dynamic: toMap(dynamic?.payload?.dynamic ?? []), semi_static: toMap(semistatic?.payload?.semi_static ?? [])}}
    {@const merchEndPoint = layout.payload?.layout.find(l => /item_merch/.test(l.endpoint ?? ""))?.endpoint ?? ""}

		<div class="full-width">
		
			{#if dynamic && !dynamic.loading && !dynamic.payload?.dynamic.length}
				<OutOfRange location={searchParams} />
			{:else if layout.loading}
				<LoadingIcons />
			{:else if layout.error}
				<div class="message error body-m book">
					{extractErrorMessage(layout.error, 'Something went wrong')}
				</div>
			{:else if page.url.searchParams.get(KEY_LANDING_PAGE)}
				<!-- Landing pages may contain a mix of shops and restaurants -->
				{@const [, restSS, restDyn, , restDeals] = $baseLandingRestaurant$ ?? []}
				{@const [, shopSS, shopDyn, , shopDeals] = $baseLandingShop$ ?? []}

				<LandingPage {searchParams} data={[[restSS, restDyn, restDeals],[shopSS, shopDyn, shopDeals]]} exitHref={`/?${query(page.url).remove(KEY_LANDING_PAGE).toString()}`} />
			{:else if searchParams[KEY_ITEM_MERCH]}
				{@const endpoint = primaryFilters?.find(f => f.term === searchParams[KEY_PRIMARY_FILTER])?.shop_department_url ?? merchEndPoint}
				<ItemMerchGrid {searchParams} {restaurants} {endpoint}/>
			{:else if layout.payload?.layout}
				{@const isSearchQuickPick = page.url.searchParams.has('search') && !!page.url.searchParams.get(KEY_SEARCH_FOUND)}
				{@const {loading: quickPickLoading, payload: quickPick} = $quickResult$ ?? {}}
				{@const group = page.url.searchParams.get('group')}
				{@const tiles = page.url.searchParams.get(KEY_TILES)}
				{@const primary = page.url.searchParams.get(KEY_PRIMARY_FILTER)}
				{@const secondary = page.url.searchParams.get(KEY_SECONDARY_FILTER)}
				{@const showFullLandingItems = !group && !tiles && !restaurantId && !primary && !secondary && !isSearchQuickPick}
				{@const notes = dynamic?.payload?.notifications}
				{@const revDynamic = reviseDynamicTilesForSeoGroupStack(group, dynamic, semistatic?.payload?.semi_static)}
		
				{#if notes?.length}
					{#each notes as note}
						<Notification data={note} on:action={({detail}) => actionNotification(detail)} />
					{/each}
				{/if}
		
				{#if isSearchQuickPick}
					<Header>
						<h3 class="display-l bold">{quickPick?.name}</h3>
					</Header>
				{:else if !group && !tiles && !primary}
					{@const lastSearchTerm = $lastFoodSeachTerm$.value}
					{@const searchPlaceholder = vertical === Vertical.Restaurant ? 'restaurants, dishes' : 'shops, items'}
					<!-- TODO: if `lastSearchTerm` is truthy add button that calls `setLastFoodSeachTerm()` when clicked -->
						<!-- <a href={query(page.url).set('search', lastSearchTerm??'').toUrl()}>Search {lastSearchTerm ? `"${lastSearchTerm}"` : searchPlaceholder}</a> -->
		
					<h1 class="display-m bold">{verticals.find((v) => v.slug == vertical)?.title}</h1>
				{/if}
				{#each layout.payload.layout as section}
					{#if !group && !tiles && !isSearchQuickPick && (section.type === LayoutSectionType.FilterSelectors || section.type === LayoutSectionType.DepartmentSelectors)}
						<Filter {searchParams} endpoint={section.endpoint} {onLoadedPrimaryFilters} />
					{:else if primary !== 'deals' && (section.type === LayoutSectionType.AllRestaurants || section.type === LayoutSectionType.Swimlane)}
						{@const showMerchLaneRestaurants = searchParams.vertical === Vertical.Restaurant && (!primary && !secondary && !tiles && !group) && /item_merch/.test(section.endpoint ?? "") }
						{@const showMerchLaneShops = searchParams.vertical === Vertical.Store && (!secondary && !tiles && !group) && /item_merch/.test(section.endpoint ?? "") }
						{#if showMerchLaneRestaurants || showMerchLaneShops && !isSearchQuickPick }
						  <ItemMerchSwimlane {searchParams} {restaurants} endpoint={section.endpoint ?? ""} title={section.title ?? ""} />
						{/if}
						{#if section.type === LayoutSectionType.AllRestaurants}
							<Tiles
								{searchParams}
								{group}
								{semistatic}
								dynamic={revDynamic ?? dynamic}
								{deals}
								{filtered$}
								filteredList={quickPick?.ids ?? directFilterIDs}
							/>
						{/if}
					{:else if showFullLandingItems}
						<svelte:component
							this={components.find(c => c.type === section.type)?.component}
							endpoint={section.endpoint}
							{searchParams}
						/>
					{/if}
				{/each}
				{#if primary === KEY_DEALS}
					<Deals
						{searchParams}
						{deals}
						{semistatic}
						{dynamic}
					/>
				{/if}
			{/if}
		</div>
	{/if}
</div>
